<script lang="ts">
	import { Input } from '$lib/components/UI';
	import type { Auth } from '$lib/types';
	import { Briefcase, Users, X } from 'lucide-svelte';

	type Props = {
		pickupDate: Date;
		userRole: Auth['role'] | undefined;
		car: {
			id: string;
			name: string;
			photo: string;
			types: string;
			pax: number;
			luggage: number;
			description: string;
			price: number;
			koeficient: number;
			tags: string[] | null;
		};
		quoteId: string;
	};

	let { car, quoteId, userRole, pickupDate }: Props = $props();
	let total = $derived(Math.round(car.price * 1.1));
	let discount = $derived(total - car.price);
	let showOffer = $state(false);
	let offer = $state(car.price);
	let showInputOffer = $state(false);
	let inputOffer = $state<HTMLInputElement | null>(null);

	let isOfferAvailable = $derived(() => {
		const today = new Date();
		const twoWeeksFromNow = new Date(today);
		twoWeeksFromNow.setDate(today.getDate() + 14);
		return pickupDate <= twoWeeksFromNow && pickupDate >= today;
	});

	let min = $derived(Math.round(car.price * 0.85));
	let max = $derived(Math.round(car.price));

	const handleOfferChange = () => {
		if (offer < min) {
			offer = min;
		}
		if (offer > max) {
			offer = max;
		}
	};

	const handleResetOffer = () => {
		offer = car.price;
		showOffer = false;
	};
</script>

{#key car.id}
	<div class="relative w-full md:max-w-[21rem]">
		{#if car.tags && car.tags.length > 0}
			<div class="absolute top-2 left-4">
				{#each car.tags.slice(0, 1) as tag}
					<span
						class="py-1 px-2 border border-orange-600 dark:border-orange-200 text-orange-600 dark:text-orange-200 text-xs rounded-lg"
						>{tag}</span
					>
				{/each}
			</div>
		{/if}
		<div class="rounded-base bg-two flex h-full flex-col gap-y-1 p-5 text-center pt-10">
			<div class="mx-auto max-w-[300px] px-5">
				<img src="/img/cars/{car.photo}" alt={car.name} />
			</div>
			<h3 class="pt-3 text-xl font-bold">{car.name}</h3>
			<p class="text-sm text-slate-500">{car.types}</p>
			<div>
				<div class="flex justify-between items-center gap-x-2 p-5 text-sm">
					<div class="flex items-center gap-x-2">
						<Users size={20} class="text-blue-600 dark:text-blue-300" />
						{car.pax} + 1 driver
					</div>
					<div class="flex items-center gap-x-2">
						<Briefcase size={20} class="text-blue-600 dark:text-blue-300" />
						{car.luggage} pcs
					</div>
				</div>
			</div>

			<p class="text-xs">{car.description}</p>
			<div class="flex h-full flex-col justify-end">
				<div class="bg-one my-3 rounded-lg px-3 pb-2 pt-3">
					<div class="flex items-center justify-between">
						<div class="text-slate-800 dark:text-slate-400">Price</div>
						<div class="text-slate-800 dark:text-slate-400">{total} &euro;</div>
					</div>
					<div class="flex items-center justify-between text-sm">
						<div class="text-slate-700 dark:text-slate-400">Today discount</div>
						<div class="text-danger">-{discount} &euro;</div>
					</div>
					<div class="flex items-center justify-between">
						<div class="text-slate-700 dark:text-slate-400">Total per car</div>
						<div class=" text-success text-xl">{car.price} &euro;</div>
					</div>
					<div class="flex items-center justify-between text-xs">
						{#if userRole && userRole !== 'USER'}
							<p class="text-indigo-500">K{(car.koeficient * 100).toFixed(0)}%</p>
						{:else}
							<p>&nbsp;</p>
						{/if}
						<p class=" text-slate-500">* All prices include VAT</p>
					</div>
				</div>

				{#if !showOffer}
					{#if isOfferAvailable()}
						<button
							type="button"
							class="btn btn-light hover:!text-primary mb-3 !text-sm !font-normal !normal-case"
							onclick={() => (showOffer = true)}>Not happy with the price?</button
						>
					{/if}
					<a href="/booking/{quoteId}?c={car.id}" class="btn btn-secondary"
						>Book for {car.price} &euro;</a
					>
				{:else}
					<div class="mb-2">
						<Input error="">
							<div class="flex items-center">
								<div class="w-48">Your offer:</div>
								<input
									id="offer"
									bind:this={inputOffer}
									type="number"
									bind:value={offer}
									placeholder="Enter your offer"
									name="offer"
									step="1"
									{min}
									{max}
									class:hidden={!showInputOffer}
									onblur={() => (showInputOffer = false)}
									onchange={handleOfferChange}
								/>
								<button
									type="button"
									class="w-full text-left"
									class:hidden={showInputOffer}
									onclick={() => {
										showInputOffer = true;
										setTimeout(() => {
											inputOffer?.select();
										}, 100);
									}}>{offer} &euro;</button
								>
								<button type="button" onclick={handleResetOffer}>
									<X size={20} />
								</button>
							</div>
						</Input>
						<p class="mt-1 text-xs text-slate-600 dark:text-slate-400">
							Enter an offer between {min}€ and {max}€
						</p>
					</div>
					{#if offer}
						<a href="/booking/{quoteId}?c={car.id}&r={offer}" class="btn btn-secondary"
							>Request for {offer} &euro;</a
						>
					{/if}
				{/if}
			</div>
		</div>
	</div>
{/key}
